/**
 * Générateur — Automatisme : connaitre les unités d'aires et de volume.
 * Attendu ID : 1059 | Niveau : 5ème | Domaine : Puissances
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 586,
 * rangé sous Puissances). Cadrage rédigé par le sous-agent programme-maths,
 * arbitré par le prof à l'épreuve de Musk le 2026-09-09.
 *
 * Le geste : l'unité se lit dans l'exposant. cm², c'est cm × cm ; cm³, c'est
 * cm × cm × cm. Entre deux unités voisines, le facteur est 10 pour une
 * longueur, 10 × 10 = 100 pour une aire, 10 × 10 × 10 = 1 000 pour un
 * volume. L'apport propre de 1059 au parc (1028, 1029, 1030 en 6e portent
 * les aires ; 644, 949, 970 les conversions) : mêler carré et cube dans une
 * même série, et l'erreur carre_pour_cube (1 dm³ = 100 cm³).
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash       (4 lignes : trois faits nus « 1 dm³ = ⬚ cm³ » et une
 *                        ligne « 1 dm² = 1 dm × 1 dm = ⬚ cm × ⬚ cm = ⬚ cm² »,
 *                        saisie des nombres)
 *   - Carré ou cube ?   (QCM nombre « 1 dm³ = … cm³ » 4 choix, QCM unité
 *                        « 10 cm × 10 cm × 10 cm = 1 000 … » 3 choix,
 *                        sousType nombre / unite ; Vrai/Faux 3 égalités)
 * Supprimés à l'épreuve de Musk : tout ce qui convertit (coefficients,
 * décimaux, deux marches : 644, 949, 970), la capacité et le litre (949),
 * la définition du cm³ (647), le centième (1029, 1030), 10² et 10³ comme
 * nombres (928), km², ha (4e), le Relier.
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Trois paires voisines (m/dm, dm/cm, cm/mm), deux grandeurs (aire 100,
 * volume 1 000), la longueur (10) en LEURRE, une ligne au plus par série.
 * Coefficient 1, une marche, les deux sens d'écriture. Six faits : PAS de
 * sacrifice, exemption assumée comme 1047 (des faits à mémoriser, en
 * retirer un laisserait trop peu aux cartes) ; la fiche prend m/dm comme
 * la fiche 1028.
 *
 * ── Distracteurs (noms de 1028 et 1029 quand c'est la même conception) ──
 *   transposition_longueur : le 10 des longueurs posé sur l'aire ou le volume
 *                            (1 m² = 10 dm²) — L'ERREUR PRINCIPALE (DEPP 6e
 *                            2025 q.18 : quatre élèves sur dix) ;
 *   carre_pour_cube        : le facteur de l'autre exposant (1 dm³ = 100 cm³ ;
 *                            au sous-type unité : cm² pour cm³) ;
 *   rang_de_trop           : 1 000 sur une aire (1 m² = 1 000 dm²) ;
 *   saut_double            : deux marches (1 m² = 10 000 dm²) ;
 *   addition_des_dix       : l'exposant lu comme une somme ou un produit
 *                            (1 m² = 20 dm² ; 1 m³ = 30 dm³) ;
 *   sens_inverse           : le grand nombre du mauvais côté (1 cm³ =
 *                            1 000 dm³), Vrai/Faux seulement ;
 *   oubli_du_carre         : l'unité sans exposant (10 cm × 10 cm × 10 cm =
 *                            1 000 cm), sous-type unité.
 * Chaque distracteur est vérifié différent de la clé ; aucune famille imposée
 * sur une fausse (leçon 1054).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1059;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_CARRE = 'Carré ou cube ?';

// ─── Le domaine ───────────────────────────────────────────────────────────

const UNITES = ['m', 'dm', 'cm', 'mm'];
const EXPOSANT = { longueur: 1, aire: 2, volume: 3 };
const SUP = { 1: '', 2: '²', 3: '³' };
const FACTEUR = { longueur: 10, aire: 100, volume: 1000 };

/** Un item : grandeur, indice de la grande unité (0 = m), sens (1 U = f u ou f u = 1 U). */
const item = (grandeur, i, inverse = false) => ({ grandeur, i, inverse, U: UNITES[i], u: UNITES[i + 1], f: FACTEUR[grandeur] });
const unite = (it, grande) => `${grande ? it.U : it.u}${SUP[EXPOSANT[it.grandeur]]}`;
const tirerItem = (grandeur, { inverse = null, exclus = [] } = {}) => {
    for (let essai = 0; essai < 60; essai++) {
        const it = item(grandeur, randInt(0, 2), inverse ?? randomChoice([true, false]));
        if (!exclus.some((x) => x.grandeur === it.grandeur && x.i === it.i)) return it;
    }
    return null;
};

const EXPLICATIONS = {
    transposition_longueur: '10, c\'est pour les longueurs. Pour une aire, 10 × 10 = 100 ; pour un volume, 10 × 10 × 10 = 1 000.',
    carre_pour_cube: 'cm³, c\'est cm × cm × cm : trois fois 10, donc 1 000, pas 100.',
    rang_de_trop: 'cm², c\'est cm × cm : deux fois 10, donc 100, pas 1 000.',
    saut_double: 'Entre deux unités voisines, une seule marche : 100 pour une aire, 1 000 pour un volume.',
    addition_des_dix: 'Le petit 2 veut dire cm × cm, pas 2 × 10 : 10 × 10 = 100.',
    sens_inverse: 'Le grand nombre va avec la petite unité : 1 dm³ = 1 000 cm³, pas l\'inverse.',
    oubli_du_carre: 'cm × cm × cm, ça fait des cm³ : le petit 3 compte les longueurs multipliées.',
    regle: 'cm², c\'est cm × cm : entre deux unités voisines, 10 × 10 = 100. cm³, c\'est cm × cm × cm : 10 × 10 × 10 = 1 000.',
};

// ─── Distracteurs (valeurs pour « 1 U = … u ») ────────────────────────────

const tousCandidats = (it) => {
    const c = [];
    const pousser = (code, val) => { if (val !== it.f) c.push({ code, val }); };
    pousser('transposition_longueur', 10);
    // le facteur de l'autre exposant : 100 sur un volume (carre_pour_cube), 1 000 sur une aire (rang_de_trop, nom de 1028)
    if (it.grandeur === 'volume') pousser('carre_pour_cube', 100); else pousser('rang_de_trop', 1000);
    pousser('saut_double', it.f * it.f);
    pousser('addition_des_dix', 10 * EXPOSANT[it.grandeur]);
    return c;
};

/** Les familles au nombre rond (10, 100, 1 000) : les seules du Vrai/Faux, 20 ou 10 000 se rejettent sans le geste (revue). */
const RONDES = ['transposition_longueur', 'carre_pour_cube', 'rang_de_trop'];

/**
 * QCM nombre : TROIS choix (la clé et deux familles distinctes). Avec quatre
 * choix, les erreurs se répartissant deux en dessous (10, 20) et deux au-dessus
 * (1 000, 10 000) d'une aire, la clé était toujours au milieu (revue : 75 %) ;
 * à trois choix le rang de la clé se tire parmi les rangs possibles.
 */
const distracteursQcm = (it) => {
    const cands = tousCandidats(it);
    const parRang = new Map();
    for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
            const t = [cands[i], cands[j]];
            const rang = t.filter((x) => x.val < it.f).length;
            if (!parRang.has(rang)) parRang.set(rang, []);
            parRang.get(rang).push(t);
        }
    }
    if (!parRang.size) return null;
    return randomChoice(parRang.get(randomChoice([...parRang.keys()])));
};

// ─── Écritures ────────────────────────────────────────────────────────────

/** « 1 dm³ = N cm³ » ou « N cm³ = 1 dm³ », N donné en texte (le trou ou la valeur). */
const egalite = (it, N) => (it.inverse ? `${N} ${unite(it, false)} = 1 ${unite(it, true)}` : `1 ${unite(it, true)} = ${N} ${unite(it, false)}`);

// ─── Carte 1 : Série flash (4 lignes, saisie du nombre) ───────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

/**
 * Une ligne de la Série flash. Par défaut la forme NUE, le fait (« 1 dm³ =
 * ⬚ cm³ », « ⬚ cm² = 1 dm² ») : « connaître » est un automatisme déclaratif
 * (Eduscol 5e 2024 p. 7). Une ligne par série, sur une aire ou un volume,
 * porte la CHAÎNE COMPLÈTE du RA16 Grandeurs et mesures p. 3, dans son
 * ordre : « 1 dm² = 1 dm × 1 dm = ⬚ cm × ⬚ cm = ⬚ cm² ». Le « 1 dm × 1 dm »
 * est écrit en clair (d'où vient le petit 2 : un trou sur un 1 ne testerait
 * rien), les 10 sont des trous : c'est l'élève qui écrit le facteur répété,
 * deux fois pour le carré, trois fois pour le cube, c'est la puissance
 * (prof, 2026-09-09). Le dernier trou est le résultat.
 */
const ligneFlash = (it, produit = false) => {
    if (!produit) return egalite(it, `{{${it.f}}}`);
    const n = EXPOSANT[it.grandeur];
    const uns = Array(n).fill(`1 ${it.U}`).join(' × ');
    const dix = Array(n).fill(`{{10}} ${it.u}`).join(' × ');
    return `1 ${unite(it, true)} = ${uns} = ${dix} = {{${it.f}}} ${unite(it, false)}`;
};
const phraseFlash = (items, iProduit) => items.map((it, i) => ligneFlash(it, i === iProduit)).join('\n');

const payloadFlash = (items, iProduit) => {
    const payload = makeTexteTrou(phraseFlash(items, iProduit), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, transposition_longueur: EXPLICATIONS.transposition_longueur } };
    payload._meta.params = { items, iProduit };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        // une aire et un volume sur la même paire (le cas discriminant), un second volume (le neuf de 5e),
        // et une quatrième ligne : aire, ou une longueur en leurre
        const i = randInt(0, 2);
        const items = [item('aire', i, randomChoice([true, false])), item('volume', i, randomChoice([true, false]))];
        for (const g of ['volume', randomChoice(['aire', 'longueur'])]) {
            const it = tirerItem(g, { exclus: items });
            if (!it) break;
            items.push(it);
        }
        if (items.length < 4) continue;
        const melange = shuffle(items);
        // une ligne « chaîne du RA16 » à trous sur les 10, sur une aire ou un volume ; parmi les lignes nues, au moins une inversée
        const iProduit = randomChoice(melange.map((it, k) => (it.grandeur !== 'longueur' ? k : -1)).filter((k) => k >= 0));
        if (!melange.some((it, k) => k !== iProduit && it.inverse)) continue;
        return payloadFlash(melange, iProduit);
    }
    // Repli déterministe : 1 dm² = 1 dm × 1 dm = ⬚ cm × ⬚ cm = ⬚ cm² ; 1 000 cm³ = 1 dm³ ; 1 m³ = 1 000 dm³ ; 1 cm = 10 mm.
    return payloadFlash([item('aire', 1), item('volume', 1, true), item('volume', 0), item('longueur', 2)], 0);
};

// ─── Carte 2, format A : QCM nombre et QCM unité ──────────────────────────

const genQcm = () => {
    if (randomChoice([true, false])) {
        // sous-type nombre : « 1 dm³ = … cm³ », trois nombres ; le volume deux fois sur trois (le neuf de 5e, les aires sont l'entretien de 6e)
        for (let essai = 0; essai < 100; essai++) {
            const it = tirerItem(randomChoice(['volume', 'volume', 'aire']), { inverse: false });
            const faux = distracteursQcm(it);
            if (!faux) continue;
            const bon = String(it.f);
            const choix = [bon, ...faux.map((f) => String(f.val))];
            const texte = `<p><b>Complète : ${egalite(it, '…')}</b></p>`;
            const payload = makeQcm(choix, bon, texte, opts(L_CARRE));
            payload.contenu = { ...payload.contenu, sousType: 'nombre', explications: EXPLICATIONS };
            payload._meta.params = { ...it, sousType: 'nombre', distracteurs: faux.map(({ code }) => ({ code })) };
            return payload;
        }
    }
    // sous-type unité : « 10 cm × 10 cm × 10 cm = 1 000 … », trois choix cm, cm², cm³
    const grandeur = randomChoice(['aire', 'volume']);
    const it = item(grandeur, randInt(0, 2));
    const facteurs = Array(EXPOSANT[grandeur]).fill(`10 ${it.u}`).join(' × ');
    const bon = unite(it, false);
    const choix = [`${it.u}`, `${it.u}²`, `${it.u}³`];
    const codes = { [`${it.u}`]: 'oubli_du_carre', [`${it.u}²`]: 'carre_pour_cube', [`${it.u}³`]: 'carre_pour_cube' };
    const texte = `<p><b>Complète : ${facteurs} = ${it.f} …</b></p>`;
    const payload = makeQcm(choix, bon, texte, opts(L_CARRE));
    payload.contenu = { ...payload.contenu, sousType: 'unite', explications: EXPLICATIONS };
    payload._meta.params = { ...it, sousType: 'unite', distracteurs: choix.filter((ch) => ch !== bon).map((ch) => ({ code: codes[ch] })) };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 égalités, grandeurs mêlées) ──────

const affirmation = (it, vraie) => {
    if (vraie) return { ligne: egalite(it, it.f), code: null };
    const cands = tousCandidats(it).filter((x) => RONDES.includes(x.code));
    // sens_inverse : le grand nombre du mauvais côté (1 cm³ = 1 000 dm³)
    const inverse = { code: 'sens_inverse', ligne: `1 ${unite(it, false)} = ${it.f} ${unite(it, true)}` };
    const d = randomChoice([...cands.map((x) => ({ code: x.code, ligne: egalite(it, x.val) })), inverse]);
    return { ligne: d.ligne, code: d.code };
};

/**
 * La ligne « produit → unité » : d'où vient le petit 2 (prof, 2026-09-09).
 * « 1 m × 1 m = 1 m² » vraie ; fausse avec le mauvais exposant : « 1 dm × 1 dm
 * = 1 dm » (oubli_du_carre) ou « 1 cm × 1 cm × 1 cm = 1 cm² » (carre_pour_cube).
 */
const affirmationProduit = (it, vraie) => {
    const n = EXPOSANT[it.grandeur];
    const produit = Array(n).fill(`1 ${it.U}`).join(' × ');
    if (vraie) return { ligne: `${produit} = 1 ${it.U}${SUP[n]}`, code: null, produit: true };
    const faux = randomChoice([{ code: 'oubli_du_carre', exp: 1 }, { code: 'carre_pour_cube', exp: n === 2 ? 3 : 2 }]);
    return { ligne: `${produit} = 1 ${it.U}${SUP[faux.exp]}`, code: faux.code, produit: true };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const grandeurs = shuffle(['aire', 'volume', randomChoice(['aire', 'volume', 'longueur'])]);
        const items = [];
        for (const g of grandeurs) {
            const it = tirerItem(g, { exclus: items });
            if (!it) break;
            items.push(it);
        }
        if (items.length < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        // une ligne sur trois est « produit → unité », sur une aire ou un volume
        const candidatsProduit = items.map((it, i) => (it.grandeur !== 'longueur' ? i : -1)).filter((i) => i >= 0);
        const iProduit = randomChoice(candidatsProduit);
        const affs = items.map((it, i) => (i === iProduit ? affirmationProduit(it, verites[i]) : affirmation(it, verites[i])));
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_CARRE));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 1 m² = 1 000 dm² faux (rang_de_trop) ; 1 dm³ = 1 000 cm³ vrai ; 1 cm² = 10 mm² faux (transposition_longueur).
    const items = [item('aire', 0), item('volume', 1), item('aire', 2)];
    const payload = makeVraiFaux(['1 m² = 1000 dm²', '1 dm³ = 1000 cm³', '1 cm² = 10 mm²'], [1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_CARRE));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [false, true, false], distracteurs: [{ code: 'rang_de_trop' }, { code: 'transposition_longueur' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois paires voisines (m/dm, dm/cm, cm/mm), aire (100) et volume (1 000), la longueur (10) en leurre une ligne au plus ; coefficient 1, une marche, deux sens ; pas de sacrifice (exemption 1047)',
        'chaque distracteur est différent de la clé ; aucune famille imposée sur une fausse',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    carre_qcm_unique: {
        nombre: '« Complète : 1 dm³ = … cm³ », TROIS nombres (à quatre, deux erreurs dessous et deux dessus laissaient la clé au milieu 75 %), deux familles distinctes parmi transposition_longueur (10), carre_pour_cube (100, volume), rang_de_trop (1 000, aire), saut_double (10 000, 1 000 000), addition_des_dix (20, 30), tirées par rang de la clé ; mesuré à la revue',
        unite: '« Complète : 10 cm × 10 cm × 10 cm = 1 000 … », trois choix cm, cm², cm³ (oubli_du_carre, carre_pour_cube) ; le seul endroit où le produit apparaît',
    },
    carre_tableau_vf: 'trois égalités, aire et volume toujours, longueur parfois, 1 ou 2 vraies ; une ligne sur trois est « produit → unité » (1 m × 1 m = 1 m² ; fausses oubli_du_carre, carre_pour_cube : d\'où vient le petit 2, prof 2026-09-09) ; les deux autres sont des faits nus, familles au nombre rond seulement (transposition_longueur, carre_pour_cube, rang_de_trop, sens_inverse) : 20 ou 10 000 se rejetaient sans le geste (« nombre rond → vrai » 65 % à la revue)',
    flash_texte_trou_saisie: 'quatre lignes : trois faits nus (« 1 dm³ = ⬚ cm³ », déclaratif, dont une inversée) et UNE ligne « chaîne du RA16 » (« 1 dm² = 1 dm × 1 dm = ⬚ cm × ⬚ cm = ⬚ cm² » : le 1 dm × 1 dm en clair, les 10 en trous, l\'élève écrit le facteur répété, c\'est la puissance ; prof 2026-09-09) ; la même paire en ² et en ³ (discriminant), deux volumes au moins (le neuf de 5e), une longueur en leurre au plus (Eduscol vérifié 2026-09-09)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    carre_qcm_unique: genQcm,
    carre_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'carre', label: L_CARRE, formatsList: [
        { key: 'carre_qcm_unique', label: 'QCM' },
        { key: 'carre_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
